/** @file bulkFill: uploads many images and fills their slots in batches of slot.fill. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import type { EditorStore } from '../../state/editorTypes';
import type { ArtDeps, BulkOutcome, FitMode, SkippedFile, SlotImage } from './artTypes';

/** The server's batch limit is 200 commands; a bigger upload is cut into batches of this many fills. */
const BATCH = 200;
/** Uploads run this many at a time. */
const PARALLEL = 4;

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** An image with the fit it is filled with (absent for an exact size). */
type Item = SlotImage & { fit?: FitMode };

/** Uploads `items` a few at a time; the image id per item that went up (in item order), the skipped ones with the error. */
async function uploadAll(deps: ArtDeps, puppetId: string, items: Item[]): Promise<{ up: { item: Item; image: string }[]; skipped: SkippedFile[] }> {
  const slots: ({ item: Item; image: string } | undefined)[] = items.map(() => undefined);
  const skipped: SkippedFile[] = [];
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next;
      next += 1;
      const item = items[i];
      if (item === undefined) continue;
      try {
        slots[i] = { item, image: (await deps.uploadImage(puppetId, item.png)).id };
      } catch (e) {
        skipped.push({ name: item.name, reason: `upload failed: ${messageOf(e)}` });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, items.length) }, worker));
  return { up: slots.filter((s) => s !== undefined), skipped };
}

/** Sends the fills in batches; a refused batch skips its files with the first error. */
async function fillBatches(store: EditorStore, up: { item: Item; image: string }[]): Promise<BulkOutcome> {
  const filled: string[] = [];
  const skipped: SkippedFile[] = [];
  for (let at = 0; at < up.length; at += BATCH) {
    const chunk = up.slice(at, at + BATCH);
    const commands: Command[] = chunk.map(({ item, image }) => ({
      name: 'slot.fill', input: item.fit === undefined ? { slot: item.slot, image } : { slot: item.slot, image, fit: item.fit },
    }));
    try {
      const replies = await store.getState().dispatch(commands, `Fill ${String(chunk.length)} slots from files`);
      const bad = replies.find((r) => !r.ok);
      if (bad?.ok === false) throw new Error(bad.error.message);
      filled.push(...chunk.map(({ item }) => item.slot));
    } catch (e) {
      skipped.push(...chunk.map(({ item }) => ({ name: item.name, reason: `not filled: ${messageOf(e)}` })));
    }
  }
  return { filled, skipped };
}

/**
 * Uploads every item (`fit` for the ones that are not the slot's size) and fills their slots in one batch each 200
 * (one undo step); lint and preview refresh afterwards. Files that fail are `skipped`, the rest `filled`.
 */
export async function bulkFill(store: EditorStore, deps: ArtDeps, items: Item[]): Promise<BulkOutcome> {
  const puppetId = store.getState().puppet.id;
  if (puppetId === null || items.length === 0) return { filled: [], skipped: [] };
  const { up, skipped } = await uploadAll(deps, puppetId, items);
  const done = await fillBatches(store, up);
  if (done.filled.length > 0) {
    await store.getState().refreshLint();
    await store.getState().refreshPreview();
  }
  return { filled: done.filled, skipped: [...skipped, ...done.skipped] };
}
